import type { HsRenderObjects } from '../engine-new/filtering/render-objects.class';
import type { HsCamera } from '../engine-new/objects/cameras/camera.class';
import type { HsGeometry } from '../graphic-engine-new/geometry/geometry.interface';
import { HsIndexedGeometry } from '../graphic-engine-new/geometry/indexed-geometry.class';
import { HsSpriteGeometry } from '../graphic-engine-new/geometry/sprite-geometry.class';
import { HsMatrix4 } from '../math-new/objects/matrix4.class';
import { HsVector3 } from '../math-new/objects/vector3.class';
import { HsFrustum } from '../math-new/shapes/frustum.class';
import { HsGraphicConstants } from './constants/graphic-constants.namespace';
import type { HsGraphicLayer } from './objects/graphic-layer.class';
import type { HsGraphicObject } from './objects/graphic-object.class';
import { HsGraphicCollection } from './objects/graphic-collection.class';
import { HsGraphicLines } from './objects/graphic-lines.class';
import { HsGraphicPoints } from './objects/graphic-points.class';
import type { HsRaycastOutput } from './objects/raycast-output.class';
import type { HsRender } from './render.class';
import { HS_RAYCAST_MAX_CLIPPING_PLANES, HsRaycastShaderData } from './shaders/classes/raycast-shader-data.class';
import type { HsClippingStorage } from '../engine-new/storages/clipping-storage.class';
import type { HsClippingPlane } from '../engine-new/objects/clipping/clipping-plane.class';
import type { HsShader } from './shaders/classes/shader.class';
import { HsShaderObjectType } from './shaders/enums/shader-object-type.enum';

/** Smallest disc, in pixels, a point is picked as; one-pixel points would be nearly impossible to click. */
const MIN_PICK_POINT_SIZE: number = 3;

interface HsRaycastRenderTarget {
  framebuffer: WebGLFramebuffer;
  texture: WebGLTexture;
  depthRenderbuffer: WebGLRenderbuffer;
  width: number;
  height: number;
  // Undefined until a successful pass; null denotes a full-target pass.
  sampledPixel: readonly [number, number] | null | undefined;
}

export class HsRaycast {
  public layers: HsGraphicLayer[] = [];
  /** Clipping planes of the scene, so picks skip what a section cut away; the first six are honored. */
  public clippingStorage: HsClippingStorage | null = null;

  private currentGeometry: HsGeometry | null = null;
  private shaderContext: WebGL2RenderingContext | null = null;
  private readonly objectsById: Map<HsGraphicLayer, Map<number, HsGraphicObject>> = new Map();
  private readonly instanceIndicesById: Map<HsGraphicLayer, Map<number, number>> = new Map();
  private readonly renderTargets: Map<HsGraphicLayer, HsRaycastRenderTarget> = new Map();
  private readonly pixelFrustum: HsFrustum = new HsFrustum();
  private readonly pixelCameraPosition: HsVector3 = HsVector3.makeZeroVector();
  private cullToPixel: boolean = false;

  private readonly raycastShader: HsShader = new HsRaycastShaderData(HsShaderObjectType.single)
    .addDepth()
    .addClipping()
    .makeShader();
  private readonly collectionRaycastShader: HsShader = new HsRaycastShaderData(HsShaderObjectType.collection)
    .addDepth()
    .addClipping()
    .makeShader();
  private readonly pointsRaycastShader: HsShader = new HsRaycastShaderData(HsShaderObjectType.single)
    .addDepth()
    .addPoints()
    .addClipping()
    .makeShader();
  private currentRaycastShader: HsShader = this.raycastShader;
  private readonly cameraUniformShaders: Set<HsShader> = new Set();

  static readonly #matrix4Temp1: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
  static readonly #matrix4Temp2: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
  static readonly #vector3Temp1: HsVector3 = HsVector3.makeZeroVector();
  static readonly #vector3Temp2: HsVector3 = HsVector3.makeZeroVector();

  public readRaycast(gl: WebGL2RenderingContext, x: number, y: number, raycastOutput: HsRaycastOutput): void {
    raycastOutput.id = 0;
    raycastOutput.type = 0;
    raycastOutput.object = null;
    raycastOutput.instanceIndex = null;
    for (let index: number = 0; index < this.layers.length; index++) {
      const layer: HsGraphicLayer = this.layers[index];
      const target: HsRaycastRenderTarget | undefined = this.renderTargets.get(layer);
      if (target === undefined || target.sampledPixel === undefined || x < layer.getLeft() || y < layer.getTop()) {
        continue;
      }
      const camera: HsCamera = layer.camera;

      const realX: number = Math.floor(x - layer.getLeft());
      const realY: number = Math.floor(y - layer.getTop());
      if (realX < 0 || realY < 0 || realX >= target.width || realY >= target.height) {
        continue;
      }
      if (target.sampledPixel !== null && (realX !== target.sampledPixel[0] || realY !== target.sampledPixel[1])) {
        continue;
      }

      gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);

      const output: Float32Array = new Float32Array([0, 0, 0, 0]);
      gl.readBuffer(gl.COLOR_ATTACHMENT0);
      gl.readPixels(realX, realY, 1, 1, gl.RGBA, gl.FLOAT, output, 0);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);

      HsRaycast.#vector3Temp1.setFromMatrixPosition(camera.worldMatrix.values);
      HsRaycast.#vector3Temp2.set(output[0], output[1], output[2]);
      HsRaycast.#vector3Temp1.add(HsRaycast.#vector3Temp2);

      raycastOutput.id = output[3] & 0x3fffff;
      raycastOutput.type = output[3] >> 22;
      raycastOutput.position.copy(HsRaycast.#vector3Temp1);
      raycastOutput.object = this.objectsById.get(layer)?.get(raycastOutput.id) ?? null;
      raycastOutput.instanceIndex = this.instanceIndicesById.get(layer)?.get(raycastOutput.id) ?? null;
      if (raycastOutput.object !== null) {
        break;
      }
    }
  }

  public async readRaycastAsync(
    gl: WebGL2RenderingContext,
    x: number,
    y: number,
    raycastOutput: HsRaycastOutput
  ): Promise<void> {
    raycastOutput.id = 0;
    raycastOutput.type = 0;
    raycastOutput.object = null;
    raycastOutput.instanceIndex = null;
    for (const layer of this.layers) {
      const target: HsRaycastRenderTarget | undefined = this.renderTargets.get(layer);
      const realX: number = Math.floor(x - layer.getLeft());
      const realY: number = Math.floor(y - layer.getTop());
      if (
        target === undefined ||
        target.sampledPixel === undefined ||
        realX < 0 ||
        realY < 0 ||
        realX >= target.width ||
        realY >= target.height ||
        (target.sampledPixel !== null && (realX !== target.sampledPixel[0] || realY !== target.sampledPixel[1]))
      ) {
        continue;
      }
      const cameraPosition: HsVector3 = HsVector3.makeZeroVector().setFromMatrixPosition(
        layer.camera.worldMatrix.values
      );
      const objectsById: Map<number, HsGraphicObject> = new Map(this.objectsById.get(layer));
      const instanceIndicesById: Map<number, number> = new Map(this.instanceIndicesById.get(layer));
      const pixels: Float32Array = await this.readPixelAsync(gl, target.framebuffer, realX, realY);
      raycastOutput.id = pixels[3] & 0x3fffff;
      raycastOutput.type = pixels[3] >> 22;
      raycastOutput.position.copy(cameraPosition).add(HsRaycast.#vector3Temp1.set(pixels[0], pixels[1], pixels[2]));
      raycastOutput.object = objectsById.get(raycastOutput.id) ?? null;
      raycastOutput.instanceIndex = instanceIndicesById.get(raycastOutput.id) ?? null;
      if (raycastOutput.object !== null) {
        break;
      }
    }
  }

  public draw(
    gl: WebGL2RenderingContext,
    render: HsRender,
    camera: HsCamera,
    renderObjects: HsRenderObjects<HsGraphicObject, HsGraphicLayer>,
    pixel?: readonly [number, number]
  ): void {
    const layer: HsGraphicLayer = renderObjects.layer;
    const previousTarget: HsRaycastRenderTarget | undefined = this.renderTargets.get(layer);
    if (previousTarget !== undefined) {
      previousTarget.sampledPixel = undefined;
    }
    this.objectsById.get(layer)?.clear();
    this.instanceIndicesById.get(layer)?.clear();
    if (pixel !== undefined && (!Number.isFinite(pixel[0]) || !Number.isFinite(pixel[1]))) {
      throw new RangeError('Raycast pixel coordinates must be finite');
    }
    if (renderObjects.sortedObjects.length + renderObjects.unsortedObjects.length === 0) {
      return;
    }
    const sampledPixel: readonly [number, number] | null =
      pixel === undefined ? null : [Math.floor(pixel[0] - layer.getLeft()), Math.floor(pixel[1] - layer.getTop())];
    if (
      sampledPixel !== null &&
      (sampledPixel[0] < 0 ||
        sampledPixel[1] < 0 ||
        sampledPixel[0] >= layer.getWidth() ||
        sampledPixel[1] >= layer.getHeight())
    ) {
      return;
    }
    const target: HsRaycastRenderTarget | null = this.getRenderTarget(gl, layer);
    if (target === null) {
      return;
    }
    this.cullToPixel = sampledPixel !== null;
    if (sampledPixel !== null) {
      this.updatePixelFrustum(camera, target.width, target.height, sampledPixel);
    }
    if (this.shaderContext !== gl) {
      this.raycastShader.update(gl);
      this.collectionRaycastShader.update(gl);
      this.pointsRaycastShader.update(gl);
      this.shaderContext = gl;
    }
    const objectsById: Map<number, HsGraphicObject> = this.objectsById.get(layer) ?? new Map();
    this.objectsById.set(layer, objectsById);
    const instanceIndicesById: Map<number, number> = this.instanceIndicesById.get(layer) ?? new Map();
    this.instanceIndicesById.set(layer, instanceIndicesById);
    const scissorEnabled: boolean = gl.isEnabled(gl.SCISSOR_TEST);
    const cullFaceEnabled: boolean = gl.isEnabled(gl.CULL_FACE);
    const depthWriteEnabled: boolean = gl.getParameter(gl.DEPTH_WRITEMASK);
    const scissorBox: Int32Array | null = sampledPixel === null ? null : gl.getParameter(gl.SCISSOR_BOX);
    try {
      if (sampledPixel !== null) {
        gl.enable(gl.SCISSOR_TEST);
        gl.scissor(sampledPixel[0], sampledPixel[1], 1, 1);
      } else if (scissorEnabled) {
        gl.disable(gl.SCISSOR_TEST);
      }
      gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
      gl.viewport(0, 0, target.width, target.height);
      gl.disable(gl.BLEND);
      gl.enable(gl.DEPTH_TEST);
      gl.depthMask(true);
      gl.disable(gl.CULL_FACE);
      this.clearViewport(gl);
      render.disableShader(gl);
      this.currentRaycastShader = this.raycastShader;
      this.cameraUniformShaders.clear();
      this.useRaycastShader(gl, camera);
      let id: number = 1;
      for (let i: number = 0; i < renderObjects.unsortedObjects.length; i++) {
        const object: HsGraphicObject = renderObjects.unsortedObjects[i];
        id = this.renderRaycastObject(gl, camera, object, id, objectsById, instanceIndicesById);
      }
      for (let i: number = 0; i < renderObjects.sortedObjects.length; i++) {
        const object: HsGraphicObject = renderObjects.sortedObjects[i];
        id = this.renderRaycastObject(gl, camera, object, id, objectsById, instanceIndicesById);
      }
    } finally {
      this.currentGeometry = null;
      if (scissorBox !== null) {
        gl.scissor(scissorBox[0], scissorBox[1], scissorBox[2], scissorBox[3]);
      }
      if (scissorEnabled) {
        gl.enable(gl.SCISSOR_TEST);
      } else if (sampledPixel !== null) {
        gl.disable(gl.SCISSOR_TEST);
      }
      this.currentRaycastShader.disable(gl);
      gl.depthMask(depthWriteEnabled);
      if (cullFaceEnabled) {
        gl.enable(gl.CULL_FACE);
      }
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.enable(gl.BLEND);
    }
    target.sampledPixel = sampledPixel;
  }

  private async readPixelAsync(
    gl: WebGL2RenderingContext,
    framebuffer: WebGLFramebuffer,
    x: number,
    y: number
  ): Promise<Float32Array> {
    const buffer: WebGLBuffer | null = gl.createBuffer();
    if (buffer === null) {
      throw new Error('Could not allocate asynchronous picking buffer');
    }
    let fence: WebGLSync | null = null;
    try {
      gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
      gl.bindBuffer(gl.PIXEL_PACK_BUFFER, buffer);
      gl.bufferData(gl.PIXEL_PACK_BUFFER, 16, gl.STREAM_READ);
      gl.readBuffer(gl.COLOR_ATTACHMENT0);
      gl.readPixels(x, y, 1, 1, gl.RGBA, gl.FLOAT, 0);
      fence = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
      gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      if (fence === null) {
        throw new Error('Could not allocate asynchronous picking fence');
      }
      gl.flush();
      const startedAt: number = performance.now();
      while (true) {
        const status: number = gl.clientWaitSync(fence, 0, 0);
        if (status === gl.ALREADY_SIGNALED || status === gl.CONDITION_SATISFIED) {
          break;
        }
        if (status === gl.WAIT_FAILED || gl.isContextLost()) {
          throw new Error('Asynchronous picking readback failed');
        }
        if (performance.now() - startedAt >= 2000) {
          throw new Error('Asynchronous picking readback timed out');
        }
        await new Promise<void>((resolve: () => void) => setTimeout(resolve, 4));
      }
      const pixels: Float32Array = new Float32Array(4);
      gl.bindBuffer(gl.PIXEL_PACK_BUFFER, buffer);
      gl.getBufferSubData(gl.PIXEL_PACK_BUFFER, 0, pixels);
      return pixels;
    } finally {
      gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      if (fence !== null) {
        gl.deleteSync(fence);
      }
      gl.deleteBuffer(buffer);
    }
  }

  private getRenderTarget(gl: WebGL2RenderingContext, layer: HsGraphicLayer): HsRaycastRenderTarget | null {
    let target: HsRaycastRenderTarget | undefined = this.renderTargets.get(layer);
    if (target === undefined) {
      const framebuffer: WebGLFramebuffer | null = gl.createFramebuffer();
      const texture: WebGLTexture | null = gl.createTexture();
      const depthRenderbuffer: WebGLRenderbuffer | null = gl.createRenderbuffer();
      if (framebuffer === null || texture === null || depthRenderbuffer === null) {
        return null;
      }
      target = { framebuffer, texture, depthRenderbuffer, width: 0, height: 0, sampledPixel: undefined };
      this.renderTargets.set(layer, target);
    }
    const width: number = layer.getWidth();
    const height: number = layer.getHeight();
    if (target.width === width && target.height === height) {
      return target;
    }

    gl.bindTexture(gl.TEXTURE_2D, target.texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, width, height, 0, gl.RGBA, gl.FLOAT, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindRenderbuffer(gl.RENDERBUFFER, target.depthRenderbuffer);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, width, height);
    gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, target.texture, 0);
    gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, target.depthRenderbuffer);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.bindRenderbuffer(gl.RENDERBUFFER, null);
    gl.bindTexture(gl.TEXTURE_2D, null);
    target.width = width;
    target.height = height;
    return target;
  }

  private setGeometry(gl: WebGL2RenderingContext, geometry: HsGeometry): void {
    this.currentGeometry = geometry;
    let dimensions: number = HsGraphicConstants.FLOATS_IN_VEC3;
    if (geometry instanceof HsIndexedGeometry) {
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, geometry.indexBuffer);
      if (geometry instanceof HsSpriteGeometry) {
        dimensions = HsGraphicConstants.FLOATS_IN_VEC2;
      }
    }
    const positionsLocation: number = this.currentRaycastShader.getAttribute('a_positions');
    gl.bindBuffer(gl.ARRAY_BUFFER, geometry.buffer);
    gl.vertexAttribPointer(
      positionsLocation,
      dimensions,
      gl.FLOAT,
      false,
      HsGraphicConstants.FLOAT32_BYTES * dimensions,
      0
    );
    const normalsLocation: number = this.currentRaycastShader.getAttribute('a_normals');
    if (normalsLocation !== -1) {
      const offset: number = geometry.positionArrayLength * HsGraphicConstants.FLOAT32_BYTES;
      gl.vertexAttribPointer(
        normalsLocation,
        dimensions,
        gl.FLOAT,
        true,
        HsGraphicConstants.FLOAT32_BYTES * dimensions,
        offset
      );
    }
  }

  private renderRaycastObject(
    gl: WebGL2RenderingContext,
    camera: HsCamera,
    object: HsGraphicObject,
    id: number,
    objectsById: Map<number, HsGraphicObject>,
    instanceIndicesById: Map<number, number>
  ): number {
    // Lines have no area to hit; drawn as triangles they would span phantom surfaces between their vertices.
    if (!object.visible || object.isDeleted || object instanceof HsGraphicLines) {
      return id;
    }
    if (object instanceof HsGraphicPoints && object.budgetSkipped) {
      return id;
    }
    const geometry: HsGeometry = object.geometry;
    if (this.cullToPixel && !(geometry instanceof HsSpriteGeometry) && !this.intersectsPixel(object)) {
      return id;
    }
    const shader: HsShader =
      object instanceof HsGraphicCollection
        ? this.collectionRaycastShader
        : object instanceof HsGraphicPoints
        ? this.pointsRaycastShader
        : this.raycastShader;
    if (shader !== this.currentRaycastShader) {
      this.currentRaycastShader.disable(gl);
      this.currentRaycastShader = shader;
      this.currentGeometry = null;
      this.useRaycastShader(gl, camera);
    }
    if (geometry.uuid !== this.currentGeometry?.uuid) {
      this.setGeometry(gl, geometry);
    }
    shader.uniform1i(gl, 'u_id', id);
    if (object instanceof HsGraphicCollection) {
      const count: number = object.getCount();
      shader.selectInstanceMatrix4(gl, 'a_relativeMatrix', object.matrixBuffer);
      try {
        if (geometry instanceof HsIndexedGeometry) {
          gl.drawElementsInstanced(gl.TRIANGLES, geometry.indexArrayLength, gl.UNSIGNED_INT, 0, count);
        } else {
          gl.drawArraysInstanced(gl.TRIANGLES, 0, geometry.positionArrayLength / 3, count);
        }
      } finally {
        shader.deselectInstanceMatrix4(gl, 'a_relativeMatrix');
      }
      for (let instanceIndex: number = 0; instanceIndex < count; instanceIndex++) {
        objectsById.set(id + instanceIndex, object);
        instanceIndicesById.set(id + instanceIndex, instanceIndex);
      }
      return id + count;
    }
    HsRaycast.#matrix4Temp2.copy(object.worldMatrix);
    HsRaycast.#vector3Temp1.setFromMatrixPosition(camera.worldMatrix.values);
    HsRaycast.#matrix4Temp2.subtractPosition(HsRaycast.#vector3Temp1.values);
    shader.uniformMatrix4fv(gl, 'u_relativeMatrix', HsRaycast.#matrix4Temp2.values);
    if (object instanceof HsGraphicPoints) {
      // Points hit as the discs they are drawn as, so a click lands on a scanned point, not between them.
      shader.uniform1f(gl, 'u_pointSize', Math.max(object.getDrawnPointSize(), MIN_PICK_POINT_SIZE));
      const loadedCount: number = geometry.positionArrayLength / 3;
      gl.drawArrays(gl.POINTS, 0, object.drawCount === null ? loadedCount : Math.min(loadedCount, object.drawCount));
    } else if (geometry instanceof HsIndexedGeometry) {
      gl.drawElements(gl.TRIANGLES, geometry.indexArrayLength, gl.UNSIGNED_INT, 0);
    } else {
      gl.drawArrays(gl.TRIANGLES, 0, geometry.positionArrayLength / 3);
    }
    objectsById.set(id, object);
    return id + 1;
  }

  private useRaycastShader(gl: WebGL2RenderingContext, camera: HsCamera): void {
    const shader: HsShader = this.currentRaycastShader;
    shader.useProgram(gl);
    shader.enable(gl);
    if (this.cameraUniformShaders.has(shader)) {
      return;
    }
    HsRaycast.#matrix4Temp1.multiplyMatrices(camera.projectionMatrix, camera.worldMatrixInverseWithZeroPosition);
    shader.uniformMatrix4fv(gl, 'u_projectionMatrix', HsRaycast.#matrix4Temp1.values);
    shader.uniform3fv(gl, 'u_cameraPosition', camera.worldMatrix.getPositionArray());
    shader.uniform1f(gl, 'u_logDepthBufFC', HsGraphicConstants.getLogDepth(camera.far));
    shader.uniform1f(gl, 'u_alpha', 1);
    this.uploadClippingPlanes(gl, shader, camera);
    this.cameraUniformShaders.add(shader);
  }

  private uploadClippingPlanes(gl: WebGL2RenderingContext, shader: HsShader, camera: HsCamera): void {
    const planes: readonly HsClippingPlane[] = this.clippingStorage?.clippingPlanes ?? [];
    const count: number = Math.min(planes.length, HS_RAYCAST_MAX_CLIPPING_PLANES);
    shader.uniform1i(gl, 'u_clippingPlaneCount', count);
    if (count === 0) {
      return;
    }
    shader.uniform3fv(gl, 'u_clipCameraPosition', camera.worldMatrix.getPositionArray());
    const values: Float32Array = new Float32Array(HS_RAYCAST_MAX_CLIPPING_PLANES * 4);
    for (let index: number = 0; index < count; index++) {
      const plane: HsClippingPlane = planes[index];
      values.set([plane.normal.x, plane.normal.y, plane.normal.z, plane.offset], index * 4);
    }
    shader.uniform4fv(gl, 'u_raycastClippingPlanes[0]', values);
  }

  private updatePixelFrustum(camera: HsCamera, width: number, height: number, pixel: readonly [number, number]): void {
    // Expand the entire pixel, not just its center, and keep camera-relative precision.
    const x: number = width - 2 * pixel[0] - 1;
    const y: number = height - 2 * pixel[1] - 1;
    HsRaycast.#matrix4Temp1.multiplyMatrices(camera.projectionMatrix, camera.worldMatrixInverseWithZeroPosition);
    HsRaycast.#matrix4Temp2.set(width, 0, 0, 0, 0, height, 0, 0, 0, 0, 1, 0, x, y, 0, 1);
    HsRaycast.#matrix4Temp2.multiply(HsRaycast.#matrix4Temp1);
    this.pixelFrustum.setFromProjectionMatrix(HsRaycast.#matrix4Temp2);
    this.pixelCameraPosition.setFromMatrixPosition(camera.worldMatrix.values);
  }

  private intersectsPixel(object: HsGraphicObject): boolean {
    const { center, radius } = object.boundingSphere;
    if (
      !Number.isFinite(center.x) ||
      !Number.isFinite(center.y) ||
      !Number.isFinite(center.z) ||
      !Number.isFinite(radius) ||
      radius < 0
    ) {
      return true;
    }
    const x: number = center.x - this.pixelCameraPosition.x;
    const y: number = center.y - this.pixelCameraPosition.y;
    const z: number = center.z - this.pixelCameraPosition.z;
    // Only side planes: logarithmic depth is still resolved by the picking shader.
    for (let index: number = 0; index < 4; index++) {
      const { normal, offset } = this.pixelFrustum.planes[index];
      const distance: number = normal.x * x + normal.y * y + normal.z * z + offset;
      if (distance < -radius - 1e-6 * Math.max(1, Math.abs(x), Math.abs(y), Math.abs(z), radius)) {
        return false;
      }
    }
    return true;
  }

  private clearViewport(gl: WebGL2RenderingContext): void {
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.clear(gl.DEPTH_BUFFER_BIT);
    gl.clear(gl.STENCIL_BUFFER_BIT);
  }
}
